// Api.jsx

import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useWorkspace } from 'context/WorkspaceContext';
import useWorkspaceAccess from 'hooks/useWorkspaceAccess';

// Utilities
import useApi from './api/hooks/useApi';
import { cleanString, downloadFile } from './api/apiUtils';

// CSS
import './Api.css';

// Components
import ApiComposer from './api/composer/ApiComposer';
import ApiEndpoints from './api/endpoints/ApiEndpoints';
import ApiHeader from './api/header/ApiHeader';
import ApiIntegrations from './api/integrations/ApiIntegrations';
import ApiKeys from './api/keys/ApiKeys';
import Section from 'components/section/Section';
import Tabs from 'components/tabs/Tabs';
import { getWorkspaceOpenApiSpec } from 'services/api/ApiAccessService';

// Constants
const TAB_KEYS = 'keys';
const TAB_INTEGRATIONS = 'integrations';
const TAB_COMPOSER = 'composer';
const TAB_ENDPOINTS = 'endpoints';

const mapSchemaForDownload = (value) => {
  if (Array.isArray(value)) {
    return value.map((row) => mapSchemaForDownload(row));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const mappedObject = Object.entries(value).reduce((accumulator, [entryKey, entryValue]) => {
    accumulator[entryKey] = mapSchemaForDownload(entryValue);
    return accumulator;
  }, {});

  const fieldId = cleanString(value.fieldId);
  const fieldName = cleanString(value.name);

  if (fieldId && fieldId === fieldName) {
    const { fieldId: _fieldId, name: _name, ...rest } = mappedObject;
    return {
      key: fieldId,
      ...rest,
    };
  }

  return mappedObject;
};

/**
 * Api
 * 
 * This component renders the Workspace API page, allowing users to explore and interact with the API schema.
 * It includes an API composer for testing requests and an endpoints viewer for browsing available API paths.
 * 
 * @returns {ReactNode} Api component.
 */
const Api = () => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // Workspace Context
  const { activeWorkspace } = useWorkspace();

  // Node Types Context
  const { getAll: getAllNodeTypes, isReady: isNodeTypesReady } = useNodeTypes();

  // ----- STATE ----- //

  // Active Tab State
  const [activeTab, setActiveTab] = useState(TAB_INTEGRATIONS);
  const [isDownloadingOpenApi, setIsDownloadingOpenApi] = useState(false);

  // ----- MEMOIZED VALUES ----- //

  // Workspace ID (cleaned)
  const workspaceId = useMemo(() => cleanString(activeWorkspace?.key || activeWorkspace?.id), [activeWorkspace?.id, activeWorkspace?.key]);

  // Workspace access role for API key management permissions
  const { workspaceRole } = useWorkspaceAccess(activeWorkspace);

  // Tabs for the workspace API page
  const workspaceTabs = useMemo(() => ([
    {
      key: TAB_INTEGRATIONS,
      label: 'Integrations',
      onClick: () => setActiveTab(TAB_INTEGRATIONS),
    },
    {
      key: TAB_KEYS,
      label: 'API Keys',
      onClick: () => setActiveTab(TAB_KEYS),
    },
    {
      key: TAB_COMPOSER,
      label: 'Composer',
      onClick: () => setActiveTab(TAB_COMPOSER),
    },
    {
      key: TAB_ENDPOINTS,
      label: 'Endpoints',
      onClick: () => setActiveTab(TAB_ENDPOINTS),
    },
  ]), []);

  // ----- HOOKS ----- //

  // API Hook to fetch schema and related data
  const {
    loading,
    error,
    apiLimits,
    schemaDoc,
    openapiGeneratedAt,
    visibleOpenapiPathRows,
    schemaTypes,
    nodeTypeCacheReady,
  } = useApi({
    workspaceId,
    getAllNodeTypes,
    isNodeTypesReady,
  });

  // ----- CONSTANTS ----- //

  // Placeholder for workspace ID in file names when downloading schema
  const workspacePlaceholder = workspaceId || '<workspace-id>';

  // ----- HANDLERS ----- //

  /**
   * handleDownloadSchema
   * 
   * Handles the download of the API schema as a JSON file. It uses the downloadFile utility to trigger a 
   * file download in the browser.
   */
  const handleDownloadSchema = () => {

    // If there is no schema document available, do not attempt to download
    if (!schemaDoc) return;

    const mappedSchemaDoc = mapSchemaForDownload(schemaDoc);

    // Trigger the file download with a filename that includes the workspace ID (or a placeholder if not available)
    downloadFile(
      `workspace-${workspacePlaceholder}-schema.json`,
      JSON.stringify(mappedSchemaDoc, null, 2),
      'application/json;charset=utf-8',
    );
    
  };

  const handleDownloadOpenApi = async () => {
    if (!workspaceId || isDownloadingOpenApi) return;

    setIsDownloadingOpenApi(true);

    try {
      const payload = await getWorkspaceOpenApiSpec({ workspaceId });
      downloadFile(
        `workspace-${workspacePlaceholder}-openapi.json`,
        JSON.stringify(payload || {}, null, 2),
        'application/json;charset=utf-8',
      );
      toast.success('OpenAPI JSON downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to download OpenAPI JSON.');
    } finally {
      setIsDownloadingOpenApi(false);
    }
  };

  // ----- RENDER ----- //

  return (

    <Section>

      {/* CONTAINER */}
      <div className="api-container"
        style={{
          color: theme.foreground,
        }}>

        {/* HEADER */}
        <ApiHeader
          theme={theme}
          apiLimits={apiLimits}
          canDownloadOpenApi={Boolean(workspaceId) && !isDownloadingOpenApi}
          onDownloadOpenApi={handleDownloadOpenApi}
          canDownloadSchema={Boolean(schemaDoc)}
          onDownloadSchema={handleDownloadSchema}
        />

        {/* LOADING MESSAGE */}
        {loading && (
          <div className="api-message"
            style={{
              color: theme.foregroundMuted
            }}>
            Loading...
          </div>
        )}

        {/* ERROR MESSAGE */}
        {!loading && error && (
          <div className="api-message api-message-error"
            style={{
              color: theme.warning,
              borderColor: theme.warning
            }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* TABS */}
            <div className="api-tabs">
              <Tabs
                tabs={workspaceTabs}
                selectedKey={activeTab}
              />
            </div>

            {/* API KEYS */}
            {activeTab === TAB_KEYS && (
              <ApiKeys
                workspaceId={workspaceId}
                workspaceRole={workspaceRole}
                schemaTypes={schemaTypes}
              />
            )}

            {activeTab === TAB_INTEGRATIONS && (
              <ApiIntegrations
                workspaceId={workspaceId}
                workspaceRole={workspaceRole}
                schemaTypes={schemaTypes}
              />
            )}

            {/* COMPOSER */}
            {activeTab === TAB_COMPOSER && (
              <ApiComposer
                workspaceId={workspaceId}
                nodeTypeCacheReady={nodeTypeCacheReady}
                schemaTypes={schemaTypes}
              />
            )}

            {/* ENDPOINTS */}
            {activeTab === TAB_ENDPOINTS && (
              <ApiEndpoints
                theme={theme}
                visibleOpenapiPathRows={visibleOpenapiPathRows}
                openapiGeneratedAt={openapiGeneratedAt}
              />
            )}

          </>
        )}

      </div>

    </Section>

  );
};

export default Api;
